import { Box, Chip, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material"
import { forwardRef, useMemo } from "react"
import { twMerge } from "tailwind-merge"

import { AdjustmentWorkOrderPalletRole } from "../../constants"
import { toWorkOrderPallets } from "../../utils"
import { AdjustmentTypeChip } from "../AdjustmentTypeChip"

import { ProductStatusCell } from "@/components/table/ProductStatusCell"
import { DATE_FORMAT } from "@/constants/formats"
import type { AdjustmentHistoryItem } from "@/graphql/codegen/graphql"
import { InventoryWorkOrderLocationCell, InventoryWorkOrderPrintLayout } from "@/screens/Inventory/components"
import { formatDate } from "@/utils/date"
import { formatNumber } from "@/utils/number"

const PALLET_ROLE_TO_CHIP_CLASS: Record<AdjustmentWorkOrderPalletRole, string> = {
  [AdjustmentWorkOrderPalletRole.Source]: "bg-secondary-200",
  [AdjustmentWorkOrderPalletRole.Target]: "bg-tertiary-green-100",
  [AdjustmentWorkOrderPalletRole.New]: "bg-tertiary-green-100",
} as const

const PALLET_ROLE_TO_LABEL: Record<AdjustmentWorkOrderPalletRole, string> = {
  [AdjustmentWorkOrderPalletRole.Source]: "Source Pallet",
  [AdjustmentWorkOrderPalletRole.Target]: "Target Pallet",
  [AdjustmentWorkOrderPalletRole.New]: "New Pallet",
} as const

interface AdjustmentWorkOrderPrintViewProps {
  item: AdjustmentHistoryItem
}

export const AdjustmentWorkOrderPrintView = forwardRef<HTMLDivElement, AdjustmentWorkOrderPrintViewProps>(
  ({ item }, ref) => {
    const pallets = useMemo(() => toWorkOrderPallets(item.lots), [item.lots])

    return (
      <InventoryWorkOrderPrintLayout
        ref={ref}
        title={`Work Order Slip: ${item.adjustmentReferenceNbr}`}
        chip={<AdjustmentTypeChip type={item.adjustmentType} />}
        workDate={formatDate(item.date, DATE_FORMAT.US)}
        warehouseLabel={[item.warehouse.as400Id, item.warehouse.description].filter(Boolean).join(" ")}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Ref#</TableCell>
              <TableCell>Product ID</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Units</TableCell>
              <TableCell>Qty to Adjust</TableCell>
              <TableCell>Current Location</TableCell>
              <TableCell>Destination</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pallets.map((pallet) => (
              <TableRow className='align-top' key={pallet.palletNbr ?? "no-pallet"}>
                <TableCell>
                  <Box className='flex flex-col items-start gap-y-1'>
                    <Chip
                      label={PALLET_ROLE_TO_LABEL[pallet.role]}
                      className={twMerge("h-6 rounded text-xs", PALLET_ROLE_TO_CHIP_CLASS[pallet.role])}
                    />
                    {pallet.palletNbr}
                  </Box>
                </TableCell>

                <TableCell>
                  <ProductStatusCell product={item.product} />
                </TableCell>

                <TableCell>
                  <Box className='flex flex-col gap-y-1'>
                    <Box className='font-bold'>{item.product.companyProductDescription}</Box>
                    <Box>{item.product.vendorDescription}</Box>
                    {pallet.lots.map((lot) => (
                      <Box key={lot.inventoryAdjustmentLotId}>
                        Lot/Batch: {lot.lotNbr} {lot.batchNbr ? ` / ${lot.batchNbr}` : ""}
                      </Box>
                    ))}
                  </Box>
                </TableCell>

                <TableCell>
                  <Box className='flex flex-col gap-y-1 pt-[44px]'>
                    {pallet.lots.map((lot) => (
                      <Box key={lot.inventoryAdjustmentLotId}>{lot.totalPackages ?? ""}</Box>
                    ))}
                  </Box>
                </TableCell>

                <TableCell>
                  <Box className='flex flex-col gap-y-1 pt-[44px]'>
                    {pallet.lots.map((lot) => (
                      <Box key={lot.inventoryAdjustmentLotId}>
                        {lot.qtyChange! > 0 ? "+" : ""}
                        {formatNumber(lot.qtyChange!, 2, 2)}
                      </Box>
                    ))}
                  </Box>
                </TableCell>

                <TableCell>
                  {pallet.role === AdjustmentWorkOrderPalletRole.Source ? (
                    <InventoryWorkOrderLocationCell
                      warehouseShelving={pallet.warehouseShelving}
                      location={pallet.location}
                    />
                  ) : (
                    "N/A"
                  )}
                </TableCell>

                <TableCell>
                  {[AdjustmentWorkOrderPalletRole.New, AdjustmentWorkOrderPalletRole.Target].includes(pallet.role) ? (
                    <InventoryWorkOrderLocationCell
                      warehouseShelving={pallet.warehouseShelving}
                      location={pallet.location}
                    />
                  ) : (
                    "N/A"
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </InventoryWorkOrderPrintLayout>
    )
  }
)
